import { ColumnDef } from '@tanstack/react-table';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import Badge, { BadgeBg } from 'components/base/Badge';
import { ActivityLog, Job } from 'services/api/job';
import EmployeeAvatar from 'components/base/EmployeeGravatar';
import { useState } from 'react';
import CardViewModal from 'components/modules/project-management/card-view/CardViewModal';
import Avatar from 'components/base/Avatar';

export const projectListTableColumns = (
  canEditProject = false,
  canEditArchive = false
): ColumnDef<Job>[] => [
  {
    accessorKey: 'title',
    header: 'Project Name',
    cell: ({ row: { original } }) => {
      const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);

      const { title } = original;
      return (
        <>
          <div
            onClick={() => setOpenDetailsModal(true)}
            className="text-decoration-none fw-bold fs-8 cursor-pointer"
          >
            {title}
          </div>
          <CardViewModal
            show={openDetailsModal}
            handleClose={() => setOpenDetailsModal(false)}
            project={original as unknown as Job}
          />
        </>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    id: 'jobNumber',
    header: 'Job Number',
    cell: ({ row: { original } }) => {
      const num = original.jobNumber;
      if (num) {
        return <p> {num}</p>;
      }
      return (
        <p>
          <Badge variant="tag">No Number</Badge>
        </p>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    id: 'assignedTo',
    header: 'assignees',
    cell: ({ row: { original } }) => {
      const { assignedTo } = original;
      return (
        <EmployeeAvatar.Group total={assignedTo.length} size="s">
          {assignedTo.slice(0, 4).map(employee => (
            <>
              <EmployeeAvatar
                key={(employee as any).id || employee._id}
                src={employee.email ? employee.email : undefined}
                variant={employee.email ? 'image' : 'name'}
                size="s"
              >
                {!employee.email && employee.firstName[0]}
              </EmployeeAvatar>
            </>
          ))}
        </EmployeeAvatar.Group>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    header: 'Return date',
    accessorKey: 'returnDate',
    cell: ({ row: { original } }) => {
      const { returnDate } = original;
      return new Date(returnDate).toLocaleDateString();
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    header: 'Departure Date',
    accessorKey: 'departureDate',
    cell: ({ row: { original } }) => {
      const { departureDate } = original;
      return new Date(departureDate).toLocaleDateString();
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  },
  {
    accessorKey: 'task',
    header: 'Task',
    meta: {
      cellProps: { className: 'ps-3 text-900 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    },
    cell: ({ row: { original } }) => {
      const tasks = original?.tasks;
      return <p>{tasks?.map(task => task?.task).join(', ') || 'N/A'}</p>;
    }
  },
  {
    id: 'well',
    header: 'Well',
    cell: ({ row: { original } }) => {
      const name = original?.well;
      return (
        <Badge variant="default" bg={'success'}>
          {name || 'N/A'}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'rig',
    header: 'Rig',
    cell: ({ row: { original } }) => {
      const name = original?.rig?.name;
      return <strong className="text-lg">{name || 'N/A'}</strong>;
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'expectedEquipmentShipDate',
    header: 'Equipment Ship Date',
    cell: ({ row: { original } }) => {
      const name = original?.expectedEquipmentShipDate;
      return new Date(name as Date).toLocaleDateString();
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'destinationType',
    header: 'Destination Type',
    accessorFn: ({ destinationType }) => destinationType,
    cell: ({ row: { original } }) => {
      const name = original?.destinationType;
      return (
        <Badge variant="phoenix" bg={'primary'}>
          {name}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'leavingFrom',
    header: 'Leaving From',
    accessorFn: ({ leavingFrom }) => leavingFrom,
    cell: ({ row: { original } }) => {
      const name = original?.leavingFrom;
      return <strong>{name}</strong>;
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'location',
    header: 'Location',
    accessorFn: ({ location }) => location.location,
    cell: ({ row: { original } }) => {
      const name = original?.location?.location;
      return <p>{name}</p>;
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'stage',
    header: 'Job Stage',
    accessorFn: ({ jobStage }) => jobStage?.name,
    cell: ({ row: { original } }) => {
      const name = original?.jobStage?.name;
      return (
        <Badge variant="phoenix" bg={name == 'working' ? 'info' : 'primary'}>
          {name}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'supervisor',
    header: 'Supervisor',
    cell: ({ row: { original } }) => {
      const name = original?.supervisor[0]?.firstName;
      return (
        <Badge variant="phoenix" bg="info">
          {name}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },

  {
    id: 'organization',
    header: 'Client',
    cell: ({ row: { original } }) => {
      const name = original?.customer?.organizationName;
      return (
        <Badge variant="phoenix" bg="info">
          {name}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'contact',
    header: 'Contact Person',
    cell: ({ row: { original } }) => {
      const name = original?.customerRepresentative?.firstName;
      return (
        <Badge variant="phoenix" bg="info">
          {name} {original?.customerRepresentative?.lastName}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'jobType',
    header: 'Job Type',
    accessorFn: ({ jobType }) => jobType?.name,
    cell: ({ row: { original } }) => {
      const name = original?.jobType?.name;
      return (
        <Badge variant="phoenix" bg={name == 'working' ? 'info' : 'primary'}>
          {name || 'N/A'}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  },
  {
    id: 'action',
    cell: () => {
      if (!canEditProject && !canEditArchive) return null;
      return (
        <RevealDropdownTrigger>
          <RevealDropdown>
            {canEditProject && <ActionDropdownItems />}
            {canEditArchive && <ActionDropdownItems />}
          </RevealDropdown>
        </RevealDropdownTrigger>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

export const activityLogColumns = (): ColumnDef<ActivityLog>[] => [
  {
    id: 'job',
    header: 'Job',
    cell: ({ row: { original } }) => {
      const job = original.jobId;
      return (
        <div className="d-flex flex-column">
          <span className="fw-bold">{job.title}</span>
          <small className="text-muted">#{job.jobNumber}</small>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '20%' } }
    }
  },
  {
    id: 'description',
    header: 'Activity',
    cell: ({ row: { original } }) => {
      const { description, metadata, activityType } = original;

      let metadataDetails = '';

      if (activityType === 'priority_changed' && metadata) {
        const oldPriority = metadata.oldPriority ? 'High' : 'Normal';
        const newPriority = metadata.newPriority ? 'High' : 'Normal';
        metadataDetails = `Changed from ${oldPriority} to ${newPriority}`;
      } else if (activityType === 'stage_changed' && metadata) {
        const oldStage = metadata.oldStageId as
          | { _id: string; name: string }
          | undefined;
        const newStage = metadata.newStageId as
          | { _id: string; name: string }
          | undefined;

        const oldStageName =
          oldStage && typeof oldStage === 'object' && 'name' in oldStage
            ? oldStage.name
            : 'Unknown';

        const newStageName =
          newStage && typeof newStage === 'object' && 'name' in newStage
            ? newStage.name
            : 'Unknown';

        metadataDetails = `Stage changed from ${oldStageName} to ${newStageName}`;
      }

      return (
        <div className="d-flex flex-column">
          <span>{description}</span>
          {metadataDetails && (
            <small className="text-muted mt-1">{metadataDetails}</small>
          )}
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    id: 'activityType',
    header: 'Type',
    cell: ({ row: { original } }) => {
      const type = original.activityType;
      let badgeColor = 'primary';

      if (type === 'priority_changed') {
        badgeColor = 'danger';
      } else if (type === 'stage_changed') {
        badgeColor = 'success';
      }

      return (
        <Badge variant="phoenix" bg={badgeColor as BadgeBg}>
          {type.replace('_', ' ')}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '15%' } }
    }
  },
  {
    id: 'metadata',
    header: 'Changes',
    cell: ({ row: { original } }) => {
      const { metadata, activityType } = original;

      if (!metadata) return <span>No changes</span>;

      if (activityType === 'priority_changed') {
        return (
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <Badge variant="phoenix" bg="secondary" className="me-2">
                From
              </Badge>
              <Badge
                variant="phoenix"
                bg={metadata.oldPriority ? 'danger' : 'success'}
              >
                {metadata.oldPriority ? 'High Priority' : 'Normal Priority'}
              </Badge>
            </div>
            <div className="d-flex align-items-center mt-1">
              <Badge variant="phoenix" bg="secondary" className="me-2">
                To
              </Badge>
              <Badge
                variant="phoenix"
                bg={metadata.newPriority ? 'danger' : 'success'}
              >
                {metadata.newPriority ? 'High Priority' : 'Normal Priority'}
              </Badge>
            </div>
          </div>
        );
      } else if (activityType === 'stage_changed') {
        const oldStage = metadata.oldStageId as
          | { _id: string; name: string }
          | undefined;
        const newStage = metadata.newStageId as
          | { _id: string; name: string }
          | undefined;

        const oldStageName =
          oldStage && typeof oldStage === 'object' && 'name' in oldStage
            ? oldStage.name
            : 'Unknown';

        const newStageName =
          newStage && typeof newStage === 'object' && 'name' in newStage
            ? newStage.name
            : 'Unknown';

        return (
          <div className="d-flex flex-column">
            <div className="d-flex flex-column">
              <Badge variant="phoenix" bg="secondary" className="mb-1">
                Previous Stage
              </Badge>
              <span className="small">{oldStageName}</span>
            </div>
            <div className="d-flex flex-column mt-1">
              <Badge variant="phoenix" bg="secondary" className="mb-1">
                New Stage
              </Badge>
              <span className="small">{newStageName}</span>
            </div>
          </div>
        );
      }

      return <span>No metadata available</span>;
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '20%' } }
    }
  },
  {
    id: 'user',
    header: 'User',
    cell: ({ row: { original } }) => {
      const user = original.userId;
      return (
        <div className="d-flex align-items-center">
          {user && 'profileImage' in user && user.profileImage ? (
            <Avatar
              size="m"
              src={user.profileImage as string}
              className="rounded-circle me-2"
            />
          ) : (
            <EmployeeAvatar
              src={user.email}
              variant="image"
              size="s"
              className="me-2"
            />
          )}
          <span>
            {user.firstName} {user.lastName}
          </span>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '15%' } }
    }
  },
  {
    id: 'time',
    header: 'Date & Time',
    cell: ({ row: { original } }) => {
      return <span>{new Date(original.createdAt).toLocaleString()}</span>;
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '10%' } }
    }
  }
];
